import { useMutation, useQuery } from "@apollo/client";
import { CheckCircleIcon, PlusCircleIcon } from "@heroicons/react/outline";
import asanaLogo from "@static/img/asana.png";
import chatGptLogo from "@static/img/chatgpt-logo.svg";
import claudeLogo from "@static/img/claude-logo.svg";
import fellowLogo from "@static/img/fellow-logo.svg";
import firefliesLogo from "@static/img/fireflies-logo.png";
import geminiLogo from "@static/img/gemini-logo.svg";
import githubLogo from "@static/img/github.png";
import googleDriveLogo from "@static/img/google-drive.svg";
import granolaLogo from "@static/img/granola-logo.png";
import hubspotLogo from "@static/img/hubspot.png";
import jiraLogo from "@static/img/jira-icon.png";
import linearLogo from "@static/img/linear.png";
import notionLogo from "@static/img/notion.svg";
import pipedriveLogo from "@static/img/pipedrive-logo.svg";
import readAiLogo from "@static/img/read-ai-logo.svg";
import salesforceLogo from "@static/img/salesforce.png";
import slackLogo from "@static/img/slack.png";
import teamsLogo from "@static/img/teams-64.png";
import youtrackLogo from "@static/img/youtrack.svg";
import { uniq } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  CreateMergeLinkTokenMutation,
  CreateMergeLinkTokenMutationVariables,
  GetAvailableMergeIntegrationsQuery,
  GetMergeDevIntegrationsQuery,
  GetMergeDevIntegrationsQueryVariables,
  GetOrganizationIntegrationsQuery,
  GetOrganizationIntegrationsQueryVariables,
  IntegrationTypes,
} from "types/graphql-schema";

import FellowIntegrationDialog from "@apps/org-settings/components/integrations/fellow-integration-dialog";
import { useMergeIntegrationLink } from "@apps/org-settings/components/integrations/use-merge-integration-link";
import SlackPermissionsDialog, {
  type SlackPermissions,
} from "@apps/org-settings/components/slack-permissions-dialog";
import createMergeLinkTokenMutation from "@apps/org-settings/graphql/create-merge-link-token";
import getAvailableMergeIntegrationsQuery from "@apps/org-settings/graphql/get-available-merge-integrations";
import getMergeDevIntegrationsQuery from "@apps/org-settings/graphql/get-merge-dev-integrations";
import getOrganizationIntegrations from "@apps/org-settings/graphql/get-organization-integrations";
import saveFellowIntegrationMutation from "@apps/org-settings/graphql/save-fellow-integration-mutation";
import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import { classNames } from "@helpers/css";
import { getAssetUrl } from "@helpers/helpers";

const MCP_DOCS_URL = "https://topicflow.mintlify.app/mcp-server";

const fallbackIntegrationIds = [
  "slack",
  "teams",
  "github",
  "claude",
  "chatgpt",
];

const normalizeIntegrationKey = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, "");

const parseOnboardingData = (value: unknown) => {
  if (!value) {
    return {};
  }
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }
  if (typeof value === "object") {
    return value;
  }
  return {};
};

type ConnectKind =
  | "account"
  | "external"
  | "fellow"
  | "merge"
  | "mcp"
  | "native-url"
  | "slack";

type IntegrationDefinition = {
  id: string;
  name: string;
  logo: string;
  connectKind: ConnectKind;
  getConnectUrl?: (organization: any) => string;
  isConnected: (user: any, organization: any) => boolean;
  settingsUrl: (organization: any) => string;
};

type IntegrationCardModel = IntegrationDefinition & {
  configured: boolean;
  mergeCategory?: string;
};

const getOrganizationIntegrationsSettingsUrl = (organization: any) =>
  `/settings/organization/${organization.id}/integrations`;

const getAccountIntegrationsSettingsUrl = () => "/account/integrations";

const integrationDefinitions: Record<string, IntegrationDefinition> = {
  slack: {
    id: "slack",
    name: "Slack",
    logo: slackLogo,
    connectKind: "slack",
    isConnected: (_user, organization) =>
      Boolean(organization.hasSlackConnection),
    settingsUrl: getOrganizationIntegrationsSettingsUrl,
  },
  teams: {
    id: "teams",
    name: "Teams",
    logo: teamsLogo,
    connectKind: "native-url",
    getConnectUrl: (organization) =>
      `/sso/microsoft?request_transcript_scopes=true&request_calendar_scope=true&request_bot_scope=true&next=/msteams/subscribe_to_transcripts?org_id=${organization.id}&integration_link_to_org_id=${organization.id}`,
    isConnected: (_user, organization) =>
      Boolean(
        organization.hasTeamsInstallation ||
          organization.hasTeamsTranscriptConnection,
      ),
    settingsUrl: getOrganizationIntegrationsSettingsUrl,
  },
  github: {
    id: "github",
    name: "GitHub",
    logo: githubLogo,
    connectKind: "external",
    getConnectUrl: () =>
      import.meta.env.DEV
        ? "https://github.com/apps/topicflow-dev/installations/select_target"
        : "https://github.com/apps/topicflow/installations/select_target",
    isConnected: (user, organization) =>
      Boolean(organization.hasGithubInstallation || user.hasGithubAuth),
    settingsUrl: (organization) =>
      organization.hasGithubInstallation
        ? getOrganizationIntegrationsSettingsUrl(organization)
        : getAccountIntegrationsSettingsUrl(),
  },
  claude: {
    id: "claude",
    name: "Claude",
    logo: claudeLogo,
    connectKind: "mcp",
    isConnected: () => false,
    settingsUrl: getAccountIntegrationsSettingsUrl,
  },
  chatgpt: {
    id: "chatgpt",
    name: "ChatGPT",
    logo: chatGptLogo,
    connectKind: "mcp",
    isConnected: () => false,
    settingsUrl: getAccountIntegrationsSettingsUrl,
  },
  gemini: {
    id: "gemini",
    name: "Gemini",
    logo: geminiLogo,
    connectKind: "mcp",
    isConnected: () => false,
    settingsUrl: getAccountIntegrationsSettingsUrl,
  },
  linear: {
    id: "linear",
    name: "Linear",
    logo: linearLogo,
    connectKind: "native-url",
    getConnectUrl: (organization) =>
      `/sso/linear?next=${getOrganizationIntegrationsSettingsUrl(
        organization,
      )}&integration_link_to_org_id=${organization.id}`,
    isConnected: () => false,
    settingsUrl: getOrganizationIntegrationsSettingsUrl,
  },
  jira: {
    id: "jira",
    name: "Jira",
    logo: jiraLogo,
    connectKind: "account",
    getConnectUrl: () => "/sso/jira?next=/account/integrations",
    isConnected: (user) => Boolean(user.hasJiraAuth),
    settingsUrl: getAccountIntegrationsSettingsUrl,
  },
  fellow: {
    id: "fellow",
    name: "Fellow",
    logo: fellowLogo,
    connectKind: "fellow",
    isConnected: (_user, organization) =>
      Boolean(organization.hasFellowTranscriptSyncConnection),
    settingsUrl: getOrganizationIntegrationsSettingsUrl,
  },
  salesforce: {
    id: "salesforce",
    name: "Salesforce",
    logo: salesforceLogo,
    connectKind: "native-url",
    getConnectUrl: (organization) =>
      `/sso/salesforce?next=${getOrganizationIntegrationsSettingsUrl(
        organization,
      )}&integration_link_to_org_id=${organization.id}`,
    isConnected: (user) => Boolean(user.hasSalesforceAuth),
    settingsUrl: getOrganizationIntegrationsSettingsUrl,
  },
  hubspot: {
    id: "hubspot",
    name: "HubSpot",
    logo: hubspotLogo,
    connectKind: "account",
    getConnectUrl: () => "/sso/hubspot?next=/account/integrations",
    isConnected: (user) => Boolean(user.hasHubspotAuth),
    settingsUrl: getAccountIntegrationsSettingsUrl,
  },
  pipedrive: {
    id: "pipedrive",
    name: "Pipedrive",
    logo: pipedriveLogo,
    connectKind: "merge",
    isConnected: () => false,
    settingsUrl: getOrganizationIntegrationsSettingsUrl,
  },
  granola: {
    id: "granola",
    name: "Granola",
    logo: granolaLogo,
    connectKind: "account",
    isConnected: (user) => Boolean(user.hasGranolaPersonalApiToken),
    settingsUrl: getAccountIntegrationsSettingsUrl,
  },
  fireflies: {
    id: "fireflies",
    name: "Fireflies",
    logo: firefliesLogo,
    connectKind: "account",
    isConnected: (user) => Boolean(user.hasFirefliesPersonalApiToken),
    settingsUrl: getAccountIntegrationsSettingsUrl,
  },
  read_ai: {
    id: "read_ai",
    name: "Read.AI",
    logo: readAiLogo,
    connectKind: "native-url",
    getConnectUrl: (organization) =>
      `/sso/readai?next=${getOrganizationIntegrationsSettingsUrl(
        organization,
      )}&integration_link_to_org_id=${organization.id}`,
    isConnected: (user) => Boolean(user.hasReadAiAuth),
    settingsUrl: getOrganizationIntegrationsSettingsUrl,
  },
  asana: {
    id: "asana",
    name: "Asana",
    logo: asanaLogo,
    connectKind: "account",
    getConnectUrl: () => "/sso/asana?next=/account/integrations",
    isConnected: (user) => Boolean(user.hasAsanaAuth),
    settingsUrl: getAccountIntegrationsSettingsUrl,
  },
  notion: {
    id: "notion",
    name: "Notion",
    logo: notionLogo,
    connectKind: "account",
    isConnected: () => false,
    settingsUrl: getAccountIntegrationsSettingsUrl,
  },
  google_drive: {
    id: "google_drive",
    name: "Google Drive",
    logo: googleDriveLogo,
    connectKind: "account",
    isConnected: (user) => Boolean(user.hasGoogleSheetsAuth),
    settingsUrl: getAccountIntegrationsSettingsUrl,
  },
  youtrack: {
    id: "youtrack",
    name: "YouTrack",
    logo: youtrackLogo,
    connectKind: "native-url",
    getConnectUrl: (organization) =>
      `/sso/youtrack?next=${getOrganizationIntegrationsSettingsUrl(
        organization,
      )}&integration_link_to_org_id=${organization.id}`,
    isConnected: (_user, organization) =>
      Boolean(organization.hasYoutrackIntegration),
    settingsUrl: getOrganizationIntegrationsSettingsUrl,
  },
};

const orgIntegrationTypeToId: Partial<Record<IntegrationTypes, string>> = {
  [IntegrationTypes.Hubspot]: "hubspot",
  [IntegrationTypes.Jira]: "jira",
  [IntegrationTypes.Linear]: "linear",
  [IntegrationTypes.ReadAi]: "read_ai",
  [IntegrationTypes.Salesforce]: "salesforce",
  [IntegrationTypes.Teams]: "teams",
  [IntegrationTypes.Youtrack]: "youtrack",
};

const YourIntegrationsCard = ({
  integration,
  isLoading,
  onClick,
}: {
  integration: IntegrationCardModel;
  isLoading: boolean;
  onClick: () => void;
}) => {
  const configured = integration.configured;
  const StatusIcon = configured ? CheckCircleIcon : PlusCircleIcon;

  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        "flex min-h-[118px] flex-1 basis-[170px] flex-col items-start justify-center gap-2 rounded-md border p-4 text-left transition-colors",
        configured
          ? "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
          : "border-dashed border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-white",
      )}
    >
      <span
        className={classNames(
          "flex size-10 items-center justify-center rounded-lg",
          configured ? "bg-white" : "bg-gray-100",
        )}
      >
        <img
          src={getAssetUrl(integration.logo)}
          alt=""
          className="size-6 object-contain"
        />
      </span>
      <span className="flex flex-col items-start gap-1">
        <span className="text-sm font-medium leading-5 text-gray-800">
          {integration.name}
        </span>
        <span
          className={classNames(
            "flex items-center gap-1.5 text-sm leading-5",
            configured ? "text-emerald-600" : "text-gray-500",
          )}
        >
          <StatusIcon className="size-4" />
          {isLoading
            ? "Connecting..."
            : configured
              ? "Configured"
              : "Not configured"}
        </span>
      </span>
    </button>
  );
};

const YourIntegrations = ({
  currentUser,
  currentOrganization,
}: {
  currentUser: any;
  currentOrganization: any;
}) => {
  const history = useHistory();
  const [showSlackPermissionsDialog, setShowSlackPermissionsDialog] =
    useState(false);
  const [showFellowIntegrationDialog, setShowFellowIntegrationDialog] =
    useState(false);
  const [connectingMergeIntegrationId, setConnectingMergeIntegrationId] =
    useState<string | null>(null);
  const [slackPermissions, setSlackPermissions] = useState<SlackPermissions>({
    notifications: false,
    bot: false,
    ingest: false,
  });

  const organizationId = currentOrganization.id;
  const hasValidOrganization = organizationId && organizationId > 0;

  const { data: orgIntegrationsData } = useQuery<
    GetOrganizationIntegrationsQuery,
    GetOrganizationIntegrationsQueryVariables
  >(getOrganizationIntegrations, {
    skip: !hasValidOrganization,
    variables: { organizationId },
  });
  const { data: mergeIntegrationsData, refetch: refetchMergeIntegrations } =
    useQuery<
      GetMergeDevIntegrationsQuery,
      GetMergeDevIntegrationsQueryVariables
    >(getMergeDevIntegrationsQuery, {
      skip:
        !hasValidOrganization || !waffle.flag_is_active("merge_dev_enabled"),
      variables: { organizationId },
    });
  const { data: availableMergeIntegrationsData } =
    useQuery<GetAvailableMergeIntegrationsQuery>(
      getAvailableMergeIntegrationsQuery,
      {
        skip: !waffle.flag_is_active("merge_dev_enabled"),
      },
    );

  const [createMergeLinkToken, { loading: mergeLinkTokenLoading }] =
    useMutation<
      CreateMergeLinkTokenMutation,
      CreateMergeLinkTokenMutationVariables
    >(createMergeLinkTokenMutation);
  const [saveFellowIntegration, { loading: isSavingFellowIntegration }] =
    useMutation(saveFellowIntegrationMutation, {
      refetchQueries: ["getOrganizationSettings"],
    });

  const { startLink } = useMergeIntegrationLink({
    onSuccess: () => {
      setConnectingMergeIntegrationId(null);
      refetchMergeIntegrations();
    },
    successMessage: "Integration connected successfully",
  });

  const settingsUrl =
    getOrganizationIntegrationsSettingsUrl(currentOrganization);

  const onboardingIntegrationIds = useMemo<string[]>(() => {
    const onboardingData = parseOnboardingData(currentUser.onboardingData);
    const accountSetup = (onboardingData as any).account_setup;
    return Array.isArray(accountSetup?.integrationIds)
      ? accountSetup.integrationIds.filter(
          (integrationId: unknown): integrationId is string =>
            typeof integrationId === "string",
        )
      : [];
  }, [currentUser.onboardingData]);

  const connectedOrgIntegrationIds = useMemo(() => {
    const ids =
      orgIntegrationsData?.organization?.integrations.edges
        .map((edge) => edge?.node?.integrationType)
        .map((integrationType) =>
          integrationType ? orgIntegrationTypeToId[integrationType] : null,
        )
        .filter((id): id is string => Boolean(id)) || [];

    return new Set(ids);
  }, [orgIntegrationsData]);

  const connectedMergeIntegrationIds = useMemo(
    () =>
      new Set(
        (mergeIntegrationsData?.mergeDevIntegrations || [])
          .map((integration) => integration?.integrationName)
          .filter((slug): slug is string => Boolean(slug))
          .map(normalizeIntegrationKey),
      ),
    [mergeIntegrationsData],
  );

  const availableMergeIntegrationsById = useMemo(() => {
    const availableMergeIntegrations =
      availableMergeIntegrationsData?.availableMergeIntegrations || [];
    return new Map(
      availableMergeIntegrations
        .filter((integration): integration is NonNullable<typeof integration> =>
          Boolean(integration),
        )
        .flatMap((integration) => {
          const definitionId =
            Object.values(integrationDefinitions).find(
              (definition) =>
                normalizeIntegrationKey(definition.id) ===
                  normalizeIntegrationKey(integration.slug) ||
                normalizeIntegrationKey(definition.name) ===
                  normalizeIntegrationKey(integration.name),
            )?.id || integration.slug;
          const category =
            integration.categories.find((value): value is string =>
              Boolean(value),
            ) || "";

          if (!category) {
            return [];
          }

          return [[definitionId, { integration, category }] as const];
        }),
    );
  }, [availableMergeIntegrationsData]);

  const cards = useMemo(() => {
    const configuredCards = Object.values(integrationDefinitions)
      .map((definition): IntegrationCardModel => {
        const configured =
          definition.isConnected(currentUser, currentOrganization) ||
          connectedOrgIntegrationIds.has(definition.id) ||
          connectedMergeIntegrationIds.has(
            normalizeIntegrationKey(definition.id),
          );
        return { ...definition, configured };
      })
      .filter((card) => card.configured);

    const selectedCards = onboardingIntegrationIds
      .map((integrationId) => integrationDefinitions[integrationId])
      .filter((definition): definition is IntegrationDefinition =>
        Boolean(definition),
      )
      .map(
        (definition): IntegrationCardModel => ({
          ...definition,
          configured:
            definition.isConnected(currentUser, currentOrganization) ||
            connectedOrgIntegrationIds.has(definition.id) ||
            connectedMergeIntegrationIds.has(
              normalizeIntegrationKey(definition.id),
            ),
          mergeCategory: availableMergeIntegrationsById.get(definition.id)
            ?.category,
        }),
      );

    const fallbackCards = fallbackIntegrationIds.map(
      (integrationId): IntegrationCardModel => ({
        ...integrationDefinitions[integrationId],
        configured:
          integrationDefinitions[integrationId].isConnected(
            currentUser,
            currentOrganization,
          ) || connectedOrgIntegrationIds.has(integrationId),
        mergeCategory:
          availableMergeIntegrationsById.get(integrationId)?.category,
      }),
    );

    const orderedIds = uniq([
      ...configuredCards.map((card) => card.id),
      ...selectedCards.map((card) => card.id),
      ...fallbackCards.map((card) => card.id),
    ]);
    const cardsById = new Map<string, IntegrationCardModel>();
    [...configuredCards, ...selectedCards, ...fallbackCards].forEach((card) => {
      if (!cardsById.has(card.id)) {
        cardsById.set(card.id, card);
      }
    });

    return orderedIds
      .map((id) => cardsById.get(id))
      .filter((card): card is IntegrationCardModel => Boolean(card))
      .slice(0, 5);
  }, [
    availableMergeIntegrationsById,
    connectedMergeIntegrationIds,
    connectedOrgIntegrationIds,
    currentOrganization,
    currentUser,
    onboardingIntegrationIds,
  ]);

  const buildSlackConnectUrl = useCallback(
    (permissions: SlackPermissions) => {
      const baseUrl = `/sso/slack?next=${settingsUrl}&org=${organizationId}`;
      const perms = Object.entries(permissions)
        .filter(([, enabled]) => enabled)
        .map(([key]) => key)
        .join(",");
      return perms ? `${baseUrl}&perms=${encodeURIComponent(perms)}` : baseUrl;
    },
    [organizationId, settingsUrl],
  );

  const handleConnectMergeIntegration = useCallback(
    async (integration: IntegrationCardModel) => {
      const availableMergeIntegration = availableMergeIntegrationsById.get(
        integration.id,
      );
      if (!availableMergeIntegration?.category) {
        history.push(settingsUrl);
        return;
      }

      setConnectingMergeIntegrationId(integration.id);
      try {
        const result = await createMergeLinkToken({
          variables: {
            integrationSlug: availableMergeIntegration.integration.slug,
            category: availableMergeIntegration.category,
          },
        });

        if (result.data?.createMergeLinkToken?.success) {
          const linkToken = result.data.createMergeLinkToken.linkToken;
          const originId = result.data.createMergeLinkToken.endUserOriginId;
          if (linkToken && originId) {
            startLink(linkToken, originId);
            return;
          }
        }

        errorNotificationVar({
          title: "Error",
          description:
            result.data?.createMergeLinkToken?.error ||
            "Failed to create link token",
        });
        setConnectingMergeIntegrationId(null);
      } catch {
        setConnectingMergeIntegrationId(null);
        errorNotificationVar({
          title: "Error",
          description: "Failed to create link token",
        });
      }
    },
    [
      availableMergeIntegrationsById,
      createMergeLinkToken,
      history,
      settingsUrl,
      startLink,
    ],
  );

  const handleSaveFellowIntegration = useCallback(
    (apiKey: string, apiDomain: string) => {
      saveFellowIntegration({
        variables: {
          organizationId,
          fellowApiKey: apiKey,
          fellowApiDomain: apiDomain,
          fellowTranscriptSyncEnabled: true,
        },
        onCompleted: () => {
          successNotificationVar({ title: "Fellow integration saved" });
          setShowFellowIntegrationDialog(false);
        },
        onError: (error) => {
          errorNotificationVar({ title: error.message });
        },
      });
    },
    [organizationId, saveFellowIntegration],
  );

  const handleClickIntegration = useCallback(
    (integration: IntegrationCardModel) => {
      if (integration.configured) {
        history.push(integration.settingsUrl(currentOrganization));
        return;
      }

      if (integration.connectKind === "slack") {
        setSlackPermissions({
          notifications: false,
          bot: false,
          ingest: false,
        });
        setShowSlackPermissionsDialog(true);
        return;
      }

      if (integration.connectKind === "fellow") {
        setShowFellowIntegrationDialog(true);
        return;
      }

      if (integration.mergeCategory || integration.connectKind === "merge") {
        handleConnectMergeIntegration(integration);
        return;
      }

      if (integration.connectKind === "mcp") {
        window.open(MCP_DOCS_URL, "_blank", "noopener,noreferrer");
        return;
      }

      const connectUrl = integration.getConnectUrl?.(currentOrganization);
      if (connectUrl) {
        window.location.href = connectUrl;
        return;
      }

      history.push(integration.settingsUrl(currentOrganization));
    },
    [currentOrganization, handleConnectMergeIntegration, history],
  );

  if (!hasValidOrganization || !cards.length) {
    return null;
  }

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-base font-semibold leading-6 text-gray-900">
        Your Integrations
      </h2>
      <div className="flex flex-wrap gap-3">
        {cards.map((integration) => (
          <YourIntegrationsCard
            key={integration.id}
            integration={integration}
            isLoading={
              mergeLinkTokenLoading &&
              connectingMergeIntegrationId === integration.id
            }
            onClick={() => handleClickIntegration(integration)}
          />
        ))}
      </div>
      <SlackPermissionsDialog
        open={showSlackPermissionsDialog}
        initialPermissions={slackPermissions}
        onClose={() => setShowSlackPermissionsDialog(false)}
        onContinue={(permissions) => {
          setSlackPermissions(permissions);
          setShowSlackPermissionsDialog(false);
          window.location.href = buildSlackConnectUrl(permissions);
        }}
      />
      <FellowIntegrationDialog
        open={showFellowIntegrationDialog}
        initialApiDomain={currentOrganization.fellowApiDomain}
        isSaving={isSavingFellowIntegration}
        onClose={() => setShowFellowIntegrationDialog(false)}
        onSave={handleSaveFellowIntegration}
      />
    </section>
  );
};

export default YourIntegrations;
